// useViewerKeyboard — Phase 11 Step 5 Block 3.
//
// Zentralisiert die Tastatur-Shortcuts des Viewers. Der Hook mountet einen
// `keydown`-Listener am `window`, ignoriert Events wenn ein Input-Element
// fokussiert ist, und ruft die übergebenen Handler auf.
//
// Shortcut-Set:
//   - `←`  / `→`   → `onArrowLeft` / `onArrowRight` (Progress-Stub)
//   - `Escape`     → `onEscape` (Viewer schließen)
//   - `m`  / `M`   → `onModeToggle` (Phone/Immersive umschalten)
//
// Warum `m` statt `f`: Browser-Shortcut-Bias ("f" = Fullscreen-API des
// Browsers in manchen Kontexten). Immersive ist kein Browser-Fullscreen,
// also kein Namensclash — `m` ("Mode") ist eindeutig.

import { useEffect, useRef } from "react";

export interface ViewerKeyboardOptions {
  onArrowLeft?: () => void;
  onArrowRight?: () => void;
  onEscape?: () => void;
  onModeToggle?: () => void;
  /** Falls der Hook mal deaktiviert werden soll (z.B. während ein Modal
   * offen ist). Default: true. */
  enabled?: boolean;
}

function isEditableTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (el.isContentEditable) return true;
  return false;
}

export function useViewerKeyboard(options: ViewerKeyboardOptions) {
  // Ref-Wrapper, damit wir nicht bei jeder Re-Render einen neuen Listener
  // registrieren, aber immer die aktuellen Handler aufrufen.
  const ref = useRef(options);
  ref.current = options;

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (options.enabled === false) return;

    function onKey(e: KeyboardEvent) {
      if (isEditableTarget(e.target)) return;
      // Modifier-Keys durchlassen (z.B. Ctrl+M für Browser-Menü unter macOS
      // liefert andere Keys, aber wir wollen systemische Shortcuts nicht
      // blockieren).
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      const { onArrowLeft, onArrowRight, onEscape, onModeToggle } = ref.current;

      switch (e.key) {
        case "ArrowLeft":
          if (onArrowLeft) {
            e.preventDefault();
            onArrowLeft();
          }
          break;
        case "ArrowRight":
          if (onArrowRight) {
            e.preventDefault();
            onArrowRight();
          }
          break;
        case "Escape":
          if (onEscape) {
            e.preventDefault();
            onEscape();
          }
          break;
        case "m":
        case "M":
          if (onModeToggle) {
            e.preventDefault();
            onModeToggle();
          }
          break;
      }
    }

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [options.enabled]);
}
